import { StockStatusEnum } from '@petsmart-ui/sparky';

import type { CommerceStore, FulfillmentGroupsItemUI, ItemUI } from '@libs/common-node';
import type { StoreV2 } from '@libs/common-types';
import { DeliveryType, type Nullable } from '@libs/core';

import type { ItemsListItem, StockErrorItem } from '../types';
import { getStockStatusBasedOnError } from './getStockStatusBasedOnError';
import { isItemIneligibleForAsBopisSdd } from './isItemIneligibleForAsBopisSdd';
import { itemHasStockError } from './itemHasStockError';
import { CartValidationErrorKey } from './notificationMaps';
import { isProductRxMedColdItem } from './productVetRxColdItem';

type StockStatusOptions = {
    fulfillmentMethod: DeliveryType;
    currentFulfillmentMethod?: DeliveryType;
    store?: Nullable<CommerceStore | StoreV2>;
    storeAvailableQty?: number;
    globalStoreAvailability?: boolean;
    stockError?: StockErrorItem;
};

export type GetAllStockStatusesProps = {
    item: ItemUI;
    fulfillmentGroup: FulfillmentGroupsItemUI;
    selectedStore?: Nullable<StoreV2>;
    bopisStoreAvailableQty?: number;
    sddStoreAvailableQty?: number;
    globalStoreAvailability?: Record<DeliveryType, boolean | undefined>;
    stockErrors: StockErrorItem[];
};

export type GetAllStockStatuses = Record<DeliveryType, StockStatusEnum>;

export const getCaseQty = (item: ItemUI, fulfillmentMethod: DeliveryType): number =>
    fulfillmentMethod?.toLowerCase() === DeliveryType.BOPIS ? item.bopisCaseQty : item.customCaseQuantity;

/**
 * Determines if an insufficient stock error should be treated as out of stock for case-required items.
 * For case-required items, users cannot order less than a full case, so if there's insufficient
 * stock to fulfill even one case, it's effectively out of stock.
 *
 * @param caseQty - The minimum case quantity required for the item
 * @param available - The available quantity (may be undefined if not provided by API)
 * @returns true if the error should be treated as out of stock, false otherwise
 */
export const shouldTreatInsufficientStockAsOutOfStock = (caseQty: number, available?: number): boolean => {
    const isCaseRequiredItem = caseQty > 1;

    if (!isCaseRequiredItem) {
        return false;
    }

    // If we have available info, check if it's less than case quantity
    if (available !== undefined) {
        return available < caseQty;
    }

    // No available info - for case-required items, assume it's out of stock
    return true;
};

export const isCaseRequired = (item: ItemUI, fulfillmentMethod: DeliveryType): boolean => {
    if (item.customCaseQuantity) {
        const isBopisFulfillment = fulfillmentMethod?.toLowerCase() === DeliveryType.BOPIS;

        if (isBopisFulfillment) {
            return item.bopisCaseQty > 0;
        }

        return Boolean(item.isCaseRequiredSth);
    }

    return false;
};

export const isOutOfStock = (
    item: ItemUI,
    fulfillmentMethod: DeliveryType,
    storeAvailableQty: number | null = null,
    stockError: StockErrorItem | undefined = undefined
): boolean => {
    const caseRequired = isCaseRequired(item, fulfillmentMethod);
    const caseQty = caseRequired ? getCaseQty(item, fulfillmentMethod) : 1;
    const availableQty = storeAvailableQty ?? item.availableQuantity;

    const hasStockError = itemHasStockError({
        itemId: item.id,
        fulfillmentMethod,
        stockError,
    });

    if (hasStockError?.errorCode === CartValidationErrorKey.OUT_OF_STOCK) {
        return true;
    }

    return availableQty < caseQty;
};

export const isLowStock = (
    item: ItemUI,
    fulfillmentMethod: DeliveryType,
    storeAvailableQty: number | null = null,
    stockError: StockErrorItem | undefined = undefined
): boolean => {
    const caseQty = getCaseQty(item, fulfillmentMethod);
    const availableQty = storeAvailableQty ?? item.availableQuantity;

    const hasStockError = itemHasStockError({
        itemId: item.id,
        fulfillmentMethod,
        stockError,
    });

    if (
        hasStockError?.errorCode === CartValidationErrorKey.INSUFFICIENT_STOCK &&
        stockError?.available !== undefined &&
        item.quantity > stockError.available
    ) {
        return true;
    }

    return availableQty >= caseQty && item.quantity > availableQty;
};

export const isAnyOutOfStock = (items: ItemsListItem[] = []): boolean =>
    items.some(item => (item.availableQuantity === 0 ? true : isOutOfStock(item, item.fulfillmentMethod)));

export const isAnyLowStock = (items: ItemsListItem[] = []): boolean =>
    items.some(item => (item.availableQuantity > 0 ? isLowStock(item, item.fulfillmentMethod) : false));

export const getAllLowStockItems = (
    items: ItemsListItem[] = [],
    stockErrors: StockErrorItem[] = []
): ItemsListItem[] => {
    const stockErrorMap = new Map(stockErrors.map(error => [error.lineItemId, error]));

    return items.filter(
        item => item.availableQuantity > 0 && isLowStock(item, item.fulfillmentMethod, null, stockErrorMap.get(item.id))
    );
};

export const getAllOutOfStockItems = (
    items: ItemsListItem[] = [],
    stockErrors: StockErrorItem[] = []
): ItemsListItem[] => {
    const stockErrorMap = new Map(stockErrors.map(error => [error.lineItemId, error]));

    return items.filter(item => isOutOfStock(item, item.fulfillmentMethod, null, stockErrorMap.get(item.id)));
};
export const getStockStatus = (
    item: ItemUI,
    {
        fulfillmentMethod,
        currentFulfillmentMethod,
        store,
        storeAvailableQty,
        globalStoreAvailability,
        stockError,
    }: StockStatusOptions
): StockStatusEnum => {
    if (!item || globalStoreAvailability === false) {
        return StockStatusEnum.NotAvailable;
    }

    const availableQty = storeAvailableQty ?? item.availableQuantity;
    const isRxCold = isProductRxMedColdItem(item);

    const isIneligible = isItemIneligibleForAsBopisSdd(item, fulfillmentMethod, currentFulfillmentMethod);

    const bopisOff =
        fulfillmentMethod === DeliveryType.BOPIS &&
        (!item.bopisEnabled || (store && !store.attributes?.bopisEnabled) || isRxCold || isIneligible);
    const autoshipOff = fulfillmentMethod === DeliveryType.AUTOSHIP && (!item.autoshipEnabled || item.isInStoreOnly);
    const sthOff = fulfillmentMethod === DeliveryType.STH && (!item.shipToHomeEnabled || item.isInStoreOnly);
    const sddOff = fulfillmentMethod === DeliveryType.SDD && (!item.samedayDeliveryEnabled || isRxCold || isIneligible);

    if ([bopisOff, autoshipOff, sthOff, sddOff].includes(true)) {
        return StockStatusEnum.NotAvailable;
    }

    if (isLowStock(item, fulfillmentMethod, availableQty, stockError)) {
        return StockStatusEnum.FewLeftInStock;
    }

    return StockStatusEnum.InStock;
};

export const getAllStockStatuses = ({
    item,
    fulfillmentGroup,
    selectedStore,
    bopisStoreAvailableQty,
    sddStoreAvailableQty,
    globalStoreAvailability,
    stockErrors,
}: GetAllStockStatusesProps): GetAllStockStatuses => {
    const stockStatuses: GetAllStockStatuses = {
        [DeliveryType.BOPIS]: StockStatusEnum.NotAvailable,
        [DeliveryType.AUTOSHIP]: StockStatusEnum.NotAvailable,
        [DeliveryType.SDD]: StockStatusEnum.NotAvailable,
        [DeliveryType.STH]: StockStatusEnum.NotAvailable,
        [DeliveryType.STM]: StockStatusEnum.NotAvailable,
    };

    Object.values(DeliveryType).forEach(fulfillmentMethod => {
        let store: Nullable<StoreV2>;
        let storeAvailableQty: number | undefined;

        if (fulfillmentMethod === DeliveryType.BOPIS) {
            store = fulfillmentGroup.store;
            storeAvailableQty = bopisStoreAvailableQty;
        } else if (fulfillmentMethod === DeliveryType.SDD) {
            store = selectedStore;
            storeAvailableQty = sddStoreAvailableQty;
        }

        const stockStatus = getStockStatus(item, {
            fulfillmentMethod,
            currentFulfillmentMethod: fulfillmentGroup.fulfillmentMethod,
            store,
            storeAvailableQty,
            ...(globalStoreAvailability && {
                globalStoreAvailability: globalStoreAvailability[fulfillmentMethod],
            }),
        });

        const caseRequired = isCaseRequired(item, fulfillmentMethod);
        const caseQty = caseRequired ? getCaseQty(item, fulfillmentMethod) : 1;

        stockStatuses[fulfillmentMethod] = getStockStatusBasedOnError({
            itemId: item.id,
            fulfillmentMethod,
            defaultStockStatus: stockStatus,
            stockErrors,
            caseQty,
        });
    });

    return stockStatuses;
};
